import { useCallback, useEffect, useRef, useState } from 'react'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

import { ResultStep } from '@/common/components/ConnectionSteps/ResultStep'
import { getApplicationToken } from '@/common/hooks/useApplicationAuth'
import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import { useModalApplication } from '@/common/hooks/useModalApplication'
import { AuthResult } from '@/common/models/applicationConnection'
import { ApiError } from '@/lib/axios'
import { ValidationStep } from '@/modules/profile/components/ChangePasswordSteps/ValidationStep'
import { PasswordDefinitionStep } from '@/modules/profile/components/RemoveAccessSteps/PasswordDefinitionStep'
import { RemovalTypeStep } from '@/modules/profile/components/RemoveAccessSteps/RemovalTypeStep'
import { useRemoveProfileAccess } from '@/modules/profile/pages/ProfileAccess/hooks/useRemoveProfileAccess'

type FlowState =
  | 'removal-type'
  | 'validation'
  | 'password-definition'
  | 'result'
  | 'ingress-warning'
type RemovalType = 'partial' | 'total'

export function useAccessRemovalManager() {
  const { t } = useTranslation(['common', 'profile'])
  const { handleModal, updateModal, resetModal } = useModalApplication()
  const { userInfo } = useGetUserInfo()
  const { handleRemoveProfileAccess } = useRemoveProfileAccess()

  const [currentState, setCurrentState] = useState<FlowState>('removal-type')
  const [removalType, setRemovalType] = useState<RemovalType | null>(null)
  const [authResult, setAuthResult] = useState<AuthResult>({
    success: false,
    loading: false,
  })
  const [authError, setAuthError] = useState<boolean>(false)
  const [friendlyMessage, setFriendlyMessage] = useState<string | null>(null)
  const [appData, setAppData] = useState<{
    applicationAccountUserId: string
    applicationName: string
    accountName: string
    isIngress: boolean
    otherAccounts?: string[]
  } | null>(null)
  const [validationValid, setValidationValid] = useState(false)
  const [passwordDefinitionValid, setPasswordDefinitionValid] = useState(false)

  const validationFormRef = useRef<{
    serviceSolutionsPassword?: string
  } | null>(null)

  const passwordDefinitionFormRef = useRef<{
    newPassword?: string
  } | null>(null)

  const renderStep = useCallback(() => {
    if (!appData) return null

    const stepNumber =
      currentState === 'removal-type'
        ? 0
        : currentState === 'validation'
          ? 1
          : currentState === 'password-definition'
            ? 2
            : currentState === 'ingress-warning'
              ? 0
              : removalType === 'partial' && appData.isIngress
                ? 2
                : 1

    const totalSteps = removalType === 'partial' && appData.isIngress ? 2 : 1

    switch (currentState) {
      case 'ingress-warning':
        return (
          <div className="space-y-4 text-center">
            <div className="flex justify-center mb-4">
              <svg
                className="h-16 w-16 text-warning-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-neutral-800">
                {t('profile:access.ingress_warning.title')}
              </h3>
              <div className="text-neutral-700 text-left">
                <p className="text-base leading-relaxed">
                  {t('profile:access.ingress_warning.message', {
                    applicationName: appData.applicationName,
                    accountNameDisplay: appData.accountName
                      ? `${t('profile:access.ingress_warning.account_separator')}${appData.accountName}`
                      : '',
                  })}
                </p>
                {appData.otherAccounts && appData.otherAccounts.length > 0 && (
                  <div className="mt-4">
                    <p className="font-medium mb-2">
                      {t('profile:access.ingress_warning.other_accounts_title')}
                    </p>
                    <ul className="list-disc list-inside space-y-1">
                      {appData.otherAccounts.map((account, index) => (
                        <li key={index}>{account}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      case 'removal-type':
        return (
          <RemovalTypeStep
            appData={appData}
            stepInfo={{ current: stepNumber, total: totalSteps }}
            onTypeChange={setRemovalType}
            currentType={removalType}
          />
        )
      case 'validation':
        return (
          <ValidationStep
            appData={appData}
            stepInfo={{ current: stepNumber, total: totalSteps }}
            onValidationChange={setValidationValid}
            onFormChange={(values) => {
              validationFormRef.current =
                values as typeof validationFormRef.current
            }}
            context="remove_access"
            isLastStep={removalType === 'total'}
          />
        )
      case 'password-definition':
        return (
          <PasswordDefinitionStep
            appData={appData}
            stepInfo={{ current: stepNumber, total: totalSteps }}
            onValidationChange={setPasswordDefinitionValid}
            onFormChange={(values) => {
              passwordDefinitionFormRef.current =
                values as typeof passwordDefinitionFormRef.current
            }}
            isLastStep={true}
          />
        )
      case 'result':
        return (
          <ResultStep
            appData={{
              applicationId: '',
              applicationName: appData.applicationName,
              userId: '',
            }}
            authResult={authResult}
            stepInfo={{ current: stepNumber, total: totalSteps }}
            authError={authError}
            namespace="access_removal"
            accountName={appData.accountName}
            friendlyMessage={friendlyMessage}
          />
        )
      default:
        return null
    }
  }, [
    appData,
    currentState,
    authResult,
    authError,
    removalType,
    friendlyMessage,
    t,
  ])

  const handleNext = useCallback(async () => {
    if (!appData) return

    switch (currentState) {
      case 'removal-type': {
        if (!removalType) return
        setCurrentState('validation')
        setValidationValid(false)
        break
      }

      case 'validation': {
        const currentFormValues = validationFormRef.current
        if (!validationValid || !currentFormValues?.serviceSolutionsPassword) {
          return
        }

        try {
          await getApplicationToken({
            username: userInfo?.Person?.email || '',
            password: currentFormValues.serviceSolutionsPassword,
          })

          if (removalType === 'partial') {
            if (appData.isIngress) {
              setCurrentState('password-definition')
              setPasswordDefinitionValid(false)
            } else {
              setAuthResult({ success: false, loading: true })
              setAuthError(false)
              setCurrentState('result')

              try {
                await handleRemoveProfileAccess(
                  appData.applicationAccountUserId,
                  currentFormValues.serviceSolutionsPassword,
                )
                setAuthResult({ success: true, loading: false })
                setTimeout(() => {
                  resetModal()
                }, 3000)
              } catch (error) {
                setAuthResult({ success: false, loading: false })
                setAuthError(true)
                if (error instanceof AxiosError) {
                  const apiError = error.response?.data as ApiError
                  setFriendlyMessage(apiError?.friendlyMessage || null)
                }
              }
            }
          } else {
            setAuthResult({ success: false, loading: true })
            setAuthError(false)
            setCurrentState('result')

            try {
              await handleRemoveProfileAccess(appData.applicationAccountUserId)
              setAuthResult({ success: true, loading: false })
              setTimeout(() => {
                resetModal()
              }, 3000)
            } catch (error) {
              setAuthResult({ success: false, loading: false })
              setAuthError(true)
              if (error instanceof AxiosError) {
                const apiError = error.response?.data as ApiError
                setFriendlyMessage(apiError?.friendlyMessage || null)
              }
            }
          }
        } catch (error) {
          setAuthResult({ success: false, loading: false })
          setAuthError(true)
          setCurrentState('result')
          if (error instanceof AxiosError) {
            const apiError = error.response?.data as ApiError
            setFriendlyMessage(apiError?.friendlyMessage || null)
          }
        }
        break
      }

      case 'password-definition': {
        const currentFormValues = passwordDefinitionFormRef.current
        if (
          !passwordDefinitionValid ||
          !currentFormValues ||
          !currentFormValues.newPassword
        ) {
          return
        }

        setAuthResult({ success: false, loading: true })
        setAuthError(false)
        setCurrentState('result')

        try {
          await handleRemoveProfileAccess(
            appData.applicationAccountUserId,
            currentFormValues.newPassword,
          )
          setAuthResult({ success: true, loading: false })
          setTimeout(() => {
            resetModal()
          }, 3000)
        } catch (error) {
          setAuthResult({ success: false, loading: false })
          setAuthError(true)
          if (error instanceof AxiosError) {
            const apiError = error.response?.data as ApiError
            setFriendlyMessage(apiError?.friendlyMessage || null)
          }
        }
        break
      }

      case 'result': {
        setCurrentState('removal-type')
        setAuthResult({ success: false, loading: false })
        setAppData(null)
        setRemovalType(null)
        setValidationValid(false)
        setPasswordDefinitionValid(false)
        setFriendlyMessage(null)
        setAuthError(false)
        resetModal()
        return
      }

      case 'ingress-warning': {
        setCurrentState('removal-type')
        setAppData(null)
        resetModal()
        return
      }
    }
  }, [
    appData,
    currentState,
    removalType,
    validationValid,
    passwordDefinitionValid,
    userInfo?.Person?.email,
    resetModal,
    handleRemoveProfileAccess,
  ])

  const getButtonText = useCallback(() => {
    if (currentState === 'removal-type')
      return t('profile:actions.next_button.text')
    if (currentState === 'validation')
      return t('profile:actions.validate_button.text')
    if (currentState === 'password-definition')
      return t('profile:actions.remove_access_button.text')
    if (currentState === 'ingress-warning')
      return t('profile:actions.close_button.text')
    return t('profile:actions.close_button.text')
  }, [currentState, t])

  const getCancelButtonText = useCallback(() => {
    if (currentState === 'result' && authError)
      return t('profile:actions.back_button.text')
    if (currentState === 'validation' || currentState === 'password-definition')
      return t('profile:actions.back_button.text')
    return undefined
  }, [currentState, authError, t])

  const handleBack = useCallback(() => {
    if (currentState === 'validation') {
      setCurrentState('removal-type')
      setValidationValid(false)
      setAuthError(false)
      setFriendlyMessage(null)
    } else if (currentState === 'password-definition') {
      setCurrentState('validation')
      setPasswordDefinitionValid(false)
    } else if (currentState === 'result' && authError) {
      setCurrentState('validation')
      setAuthError(false)
      setAuthResult({ success: false, loading: false })
      setFriendlyMessage(null)
    }
  }, [currentState, authError])

  useEffect(() => {
    if (!appData) return

    const shouldShowButtons =
      currentState === 'result' ? !authResult.loading : true

    const isDisabled =
      currentState === 'removal-type'
        ? !removalType
        : currentState === 'validation'
          ? !validationValid
          : currentState === 'password-definition'
            ? !passwordDefinitionValid
            : currentState === 'ingress-warning'
              ? false
              : false

    updateModal({
      title: t('profile:access.modal.remove_access.title'),
      description: renderStep(),
      confirmText: getButtonText(),
      cancelText: getCancelButtonText(),
      showButtons: shouldShowButtons,
      onConfirm: handleNext,
      onCancel: getCancelButtonText() ? handleBack : undefined,
      disabled: isDisabled,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentState,
    authResult.loading,
    appData,
    removalType,
    validationValid,
    passwordDefinitionValid,
    t,
    getButtonText,
    getCancelButtonText,
    handleBack,
  ])

  const handleRemoveApplicationAccess = useCallback(
    async (
      applicationAccountUserId: string,
      applicationName: string,
      accountName: string,
      isIngress: boolean,
    ) => {
      setAppData({
        applicationAccountUserId,
        applicationName,
        accountName,
        isIngress,
      })
      setCurrentState('removal-type')
      setRemovalType(null)
      setValidationValid(false)
      setPasswordDefinitionValid(false)
      setAuthError(false)
      setFriendlyMessage(null)

      handleModal({
        title: t('profile:access.modal.remove_access.title'),
        description: renderStep(),
        showButtons: true,
        onConfirm: handleNext,
      })
    },
    [handleModal, renderStep, handleNext, t],
  )

  const handleIngressAccountWarning = useCallback(
    (applicationName: string, accountName: string, otherAccounts: string[]) => {
      setAppData({
        applicationAccountUserId: '',
        applicationName,
        accountName,
        isIngress: true,
        otherAccounts,
      })
      setCurrentState('ingress-warning')
      setRemovalType(null)
      setValidationValid(false)
      setPasswordDefinitionValid(false)
      setAuthError(false)
      setFriendlyMessage(null)

      handleModal({
        title: t('profile:access.ingress_warning.title'),
        description: renderStep(),
        showButtons: true,
        onConfirm: handleNext,
      })
    },
    [handleModal, renderStep, handleNext, t],
  )

  return {
    handleRemoveApplicationAccess,
    handleIngressAccountWarning,
  }
}
